import {Game} from "../Game";
import {gsap} from "gsap";
import {GameModeNames} from "../server/GameModeNames";

export class Wall extends PIXI.Container {
    private _animations: PIXI.spine.Spine[];

    constructor() {
        super();
        this.name = "Wall";

        this.position.set(Game.REEL_AREA_X, Game.REEL_AREA_Y);

        this.createFrames();
        this.onGameModeChange(GameModeNames.NORMAL);

        if (Game.model.isRestore && Game.hasGridExpanded) {
            this.onGridExpansion(0, Game.gridSize);
        } else {
            this.positionWallFrames(0, "6by6");
        }
    }

    // #366
    public onGameModeChange(nextMode: string): void {
        let skinName: string = "main";

        if (!nextMode.includes("NORMAL")) {
            if (Game.model.data.mayhemSpin) {
                skinName = "mayhem";
            } else if (nextMode.includes("FREESPIN")) {
                skinName = "FS";
            }
        }

        this._animations.forEach((animation: PIXI.spine.Spine) => {
            if (skinName === "FS") {
                animation.state.setAnimation(0, "loop", true);
            } else {
                animation.state.setAnimation(0, "base", false);
            }

            animation.skeleton.setSkinByName(skinName);
        });
    }

    public onGridExpansion(duration: number, gridSize: string): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        tl.add(this.positionWallFrames(duration, gridSize), 0);
        this._animations.forEach((animation: PIXI.spine.Spine) => {
            tl.to(animation.scale, {
                x: Game.scaleFactor,
                y: Game.scaleFactor,
                duration: duration
            }, 0);
        });

        return tl;
    }

    private createFrames(): void {
        if (this._animations && this._animations.length) {
            this.removeChildren();
        }

        this._animations = [];

        this._animations.push(new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("wallTop")));
        this._animations.push(new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("wallRight")));
        this._animations.push(new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("wallBottom")));
        this._animations.push(new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("wallLeft")));
        this._animations.push(new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("wallTopLeft")));
        this._animations.push(new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("wallTopRight")));
        this._animations.push(new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("wallBottomRight")));
        this._animations.push(new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("wallBottomLeft")));

        for (let animation of this._animations) {
            animation.alpha = 1;
            this.addChild(animation);
        }
    }

    private positionWallFrames(duration: number, gridSize: string): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            [[tlx, tly], [trX, trY], [brX, brY], [blX, blY], [tfX, tfY], [rfX, rfY], [bfX, bfY], [lfX, lfY]] = this.getLeftTopFramePositions(gridSize);

        const wallTop: PIXI.spine.Spine = this._animations[0];
        const wallRight: PIXI.spine.Spine = this._animations[1];
        const wallBottom: PIXI.spine.Spine = this._animations[2];
        const wallLeft: PIXI.spine.Spine = this._animations[3];

        tl.to(wallTop, {
            x: tfX,
            y: tfY,
            duration: duration
        }, 0);
        tl.to(wallRight, {
            x: rfX,
            y: rfY,
            duration: duration
        }, 0);
        tl.to(wallBottom, {
            x: bfX,
            y: bfY,
            duration: duration
        }, 0);
        tl.to(wallLeft, {
            x: lfX,
            y: lfY,
            duration: duration
        }, 0);

        const topLeftCorner: PIXI.spine.Spine = this._animations[4],
            topRightCorner: PIXI.spine.Spine = this._animations[5],
            bottomRightCorner: PIXI.spine.Spine = this._animations[6],
            bottomLeftCorner: PIXI.spine.Spine = this._animations[7];

        tl.to(topLeftCorner, {
            x: tlx,
            y: tly,
            duration: duration
        }, 0);
        tl.to(topRightCorner, {
            x: trX,
            y: trY,
            duration: duration
        }, 0);
        tl.to(bottomRightCorner, {
            x: brX,
            y: brY,
            duration: duration
        }, 0);
        tl.to(bottomLeftCorner, {
            x: blX,
            y: blY,
            duration: duration
        }, 0);

        return tl;
    }

    private getLeftTopFramePositions(gridName: string): number[][] {
        switch (gridName) {
            case "8by6":
                return [
                    [-39, 45], // top left
                    [575, 45], // top right
                    [575, 445], // bottom right
                    [-39, 445], // bottom left
                    [270, -35], // top
                    [663, 245], // right
                    [270, 525], // bottom
                    [-127, 245], // left
                ];
            case "6by8":
                return [
                    [46, -55], // top left
                    [485, -55], // top right
                    [485, 505], // bottom right
                    [46, 505], // bottom left
                    [265, -135], // top
                    [573, 225], // right
                    [265, 585], // bottom
                    [-42, 220], // left
                ];
            case "8by8":
                return [
                    [-38, -54], // top left
                    [575, -54], // top right
                    [575, 505], // bottom right
                    [-38, 505], // bottom left
                    [270, -134], // top
                    [663, 230], // right
                    [270, 585], // bottom
                    [-126, 230], // left
                ];
            case "9by8":
                return [
                    [-35, -20], // top left
                    [580, -20], // top right
                    [580, 470], // bottom right
                    [-35, 470], // bottom left
                    [276, -90], // top
                    [657, 225], // right
                    [276, 540], // bottom
                    [-112, 226], // left
                ];
            case "8by9":
                return [
                    [0, -60], // top left
                    [538, -60], // top right
                    [538, 500], // bottom right
                    [0, 500], // bottom left
                    [270, -130], // top
                    [615, 220], // right
                    [270, 570], // bottom
                    [-77, 220], // left
                ];
            case "9by9":
                return [
                    [-40, -60], // top left
                    [575, -60], // top right
                    [575, 500], // bottom right
                    [-40, 500], // bottom left
                    [270, -130], // top
                    [652, 222], // right
                    [260, 570], // bottom
                    [-117, 220], // left
                ];
        }
        return [
            [-3, 0], // top left
            [545, 0], // top right
            [545, 500], // bottom right
            [-4, 500], // bottom left
            [270, -100], // top
            [655, 250], // right
            [270, 600], // bottom
            [-113, 250], // left
        ]
    }
}